import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Building, Layers, Zap, Palette, ChevronRight, Shield, Home, Wrench, Paintbrush, Hammer, HardHat, Leaf, Cog, DraftingCompass, Search, Download, ArrowUpDown, Filter } from 'lucide-react';
import { mockLCAData } from '../data/mockLCAData';

const ComponentBreakdown = ({ components, selectedImpact, componentsByCategory, lcaData }) => {
  const [expandedCategory, setExpandedCategory] = useState('All Categories');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('value');
  const [sortDirection, setSortDirection] = useState('desc');

  const categoryIcons = {
    'Structure': Hammer,        // Hammer represents structural foundation/base
    'Envelope': Home,           // Home represents building envelope/shell
    'MEP': Cog,                 // Cog represents mechanical systems
    'Finishes': Paintbrush,     // Paintbrush represents finishing touches
    'Foundation': DraftingCompass, // Drafting compass for foundation design
    'Roofing': Shield,          // Shield for roofing protection
    'Electrical': Zap,          // Zap for electrical systems
    'HVAC': Wrench,             // Wrench for HVAC maintenance
    'Plumbing': Layers,         // Layers for plumbing systems
    'Interior': Palette,        // Palette for interior design
    'Exterior': Building,       // Building for exterior elements
    'Landscaping': Leaf,        // Leaf for landscaping
    'Other': Building           // Default icon for unknown categories
  };

  const categoryColors = {
    'Structure': 'from-teal-400 to-teal-600',      // Teal for structural strength
    'Envelope': 'from-yellow-400 to-yellow-600',   // Yellow for building envelope
    'MEP': 'from-blue-700 to-blue-900',            // Navy Blue for mechanical systems
    'Finishes': 'from-orange-500 to-orange-700',   // Orange for finishing touches
    'Foundation': 'from-blue-600 to-blue-800',     // Dark Blue for foundation
    'Roofing': 'from-cyan-500 to-cyan-700',        // Teal Blue for roofing
    'Electrical': 'from-yellow-300 to-yellow-500', // Light Yellow for electrical
    'HVAC': 'from-orange-400 to-orange-600',       // Light Orange for HVAC
    'Plumbing': 'from-sky-500 to-sky-700',         // Sky Blue for plumbing
    'Interior': 'from-green-500 to-green-700',     // Green for interior
    'Exterior': 'from-orange-500 to-orange-700',   // Orange for exterior
    'Landscaping': 'from-green-500 to-green-700',  // Green for landscaping
    'Other': 'from-gray-400 to-gray-600'            // Default color for unknown categories
  };

  // Function to get badge colors that match the category box gradient colors
  const getCategoryBadgeColor = (categoryName) => {
    const gradientColors = categoryColors[categoryName];
    if (!gradientColors) return 'bg-gray-100 text-gray-700';
    
    // Map gradient colors to matching solid badge colors
    if (gradientColors.includes('teal')) return 'bg-teal-100 text-teal-700';
    if (gradientColors.includes('yellow')) return 'bg-yellow-100 text-yellow-700';
    if (gradientColors.includes('blue-700') || gradientColors.includes('blue-800')) return 'bg-blue-100 text-blue-700';
    if (gradientColors.includes('orange')) return 'bg-orange-100 text-orange-700';
    if (gradientColors.includes('cyan')) return 'bg-cyan-100 text-cyan-700';
    if (gradientColors.includes('green')) return 'bg-green-100 text-green-700';
    if (gradientColors.includes('sky')) return 'bg-sky-100 text-sky-700';
    if (gradientColors.includes('stone')) return 'bg-stone-100 text-stone-700';
    if (gradientColors.includes('emerald')) return 'bg-emerald-100 text-emerald-700';
    
    return 'bg-gray-100 text-gray-700'; // Default fallback
  };

  const getCategoryTotal = (category) => {
    return componentsByCategory[category]?.reduce(
      (sum, component) => sum + (component.impacts[selectedImpact] || 0), 0
    ) || 0;
  };

  // Convert kg to tonnes for display
  const convertToTonnes = (value) => {
    if (value >= 1000) {
      return (value / 1000).toFixed(2) + ' t';
    } else {
      return value.toLocaleString() + ' kg';
    }
  };

  // Use actual data if available, fallback to mock data
  const dataSource = lcaData || mockLCAData;
  const selectedImpactInfo = dataSource.impactCategories.find(cat => cat.id === selectedImpact);

  // Safety check for componentsByCategory
  if (!componentsByCategory || Object.keys(componentsByCategory).length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No components found. Please check your data.</p>
      </div>
    );
  }

  // Debug logging to help identify issues
  console.log('ComponentBreakdown - componentsByCategory:', componentsByCategory);
  console.log('ComponentBreakdown - available categories:', Object.keys(componentsByCategory));

  // Order categories according to the specified structure: Structure → Envelope → Finishes → MEP → Other categories
  const getOrderedCategories = () => {
    const categoryOrder = ['Structure', 'Envelope', 'Finishes', 'MEP'];
    const orderedCategories = {};
    
    // First add categories in the preferred order
    categoryOrder.forEach(category => {
      if (componentsByCategory[category]) {
        orderedCategories[category] = componentsByCategory[category];
      }
    });
    
    // Then add any remaining categories that weren't in the preferred order
    Object.keys(componentsByCategory).forEach(category => {
      if (!categoryOrder.includes(category)) {
        orderedCategories[category] = componentsByCategory[category];
      }
    });
    
    return orderedCategories;
  };

  // Get all components for the "All Categories" box
  const getAllComponents = () => {
    const allComponents = [];
    Object.values(componentsByCategory).forEach(categoryComponents => {
      allComponents.push(...categoryComponents);
    });
    
    // Sort all components by impact value in descending order
    return allComponents.sort((a, b) => {
      const impactA = a.impacts[selectedImpact] || 0;
      const impactB = b.impacts[selectedImpact] || 0;
      return impactB - impactA;
    });
  };

  // Filtered and sorted data for All Categories
  const filteredAllComponents = useMemo(() => {
    let filtered = getAllComponents().filter(component =>
      component.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (sortField === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else if (sortField === 'category') {
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
      } else if (sortField === 'value') {
        aValue = a.impacts[selectedImpact] || 0;
        bValue = b.impacts[selectedImpact] || 0;
      } else if (sortField === 'percentage') {
        aValue = (a.impacts[selectedImpact] || 0) / dataSource.totalImpacts[selectedImpact];
        bValue = (b.impacts[selectedImpact] || 0) / dataSource.totalImpacts[selectedImpact];
      } else {
        aValue = a.impacts[selectedImpact] || 0;
        bValue = b.impacts[selectedImpact] || 0;
      }
      
      if (typeof aValue === 'string') {
        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      } else {
        if (sortDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
    });

    return filtered;
  }, [componentsByCategory, selectedImpact, searchTerm, sortField, sortDirection, dataSource.totalImpacts]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Export CSV functionality
  const exportAllCategoriesData = () => {
    const csvContent = [
      ['Component', 'Category', `${selectedImpactInfo?.shortName || 'Impact'} (CO2-eq)`, 'Percentage'].join(','),
      ...filteredAllComponents.map(component => {
        const impactValue = component.impacts[selectedImpact] || 0;
        const percentage = (impactValue / dataSource.totalImpacts[selectedImpact]) * 100;
        return [
          component.name,
          component.category,
          impactValue,
          percentage.toFixed(2) + '%'
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lca-all-categories-${selectedImpact}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const orderedCategories = getOrderedCategories();

  return (
    <div className="space-y-6">
      {/* Category Overview */}
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 items-start">
        {/* All Categories Box - First (Visually Distinct with Green Theme) */}
        <Card
          className="h-36 cursor-pointer border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200"
          style={{ transform: "none", animation: "none" }}
          onClick={() => setExpandedCategory('All Categories')}
        >
          <CardContent
            className="relative flex h-full flex-col items-center justify-center gap-2 p-3 text-center"
            style={{ transform: "none", animation: "none" }}
          >
            <div className="absolute right-2 top-2">
              <ChevronRight className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <h3 className="text-sm font-bold leading-tight text-emerald-800">All Categories</h3>
              <p className="text-sm font-bold leading-tight text-emerald-900 truncate">
                {convertToTonnes(dataSource.totalImpacts[selectedImpact])}
              </p>
              <p className="text-[9px] leading-tight text-emerald-600 truncate">
                CO2-eq (displayed in tonnes) (100%)
              </p>
              <Badge
                variant="secondary"
                className="text-[9px] leading-none bg-emerald-100 text-emerald-700 border-emerald-200"
              >
                {getAllComponents().length} components
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Individual Category Boxes */}
        {Object.entries(orderedCategories).map(([categoryName, categoryComponents]) => {
          const Icon = categoryIcons[categoryName] || Building; // Fallback to Building icon
          const total = getCategoryTotal(categoryName);
          const percentage = (total / dataSource.totalImpacts[selectedImpact]) * 100;

          return (
            <Card
              key={categoryName}
              className="h-36 cursor-pointer border-0 bg-white/80 hover:bg-white/90 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,background-color] duration-200"
              style={{ transform: "none", animation: "none" }}
              onClick={() => setExpandedCategory(categoryName)}
            >
              <CardContent className="h-full p-3" style={{ transform: "none", animation: "none" }}>
                <div className="flex items-center justify-between mb-2">
                  <div
                    className={`w-8 h-8 rounded-lg bg-gradient-to-r ${
                      categoryColors[categoryName] || 'from-gray-400 to-gray-600'
                    } flex items-center justify-center`}
                  >
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
                <h3 className="font-semibold leading-tight text-gray-800 mb-1 text-sm">{categoryName}</h3>
                <div className="space-y-0.5">
                  <p className="text-sm font-bold leading-tight text-gray-900 truncate">
                    {convertToTonnes(total)}
                  </p>
                  <p className="text-[9px] leading-tight text-gray-600 truncate">
                    CO2-eq (displayed in tonnes) ({percentage.toFixed(1)}%)
                  </p>
                  <Badge variant="secondary" className="text-[9px] leading-none">
                    {categoryComponents.length} components
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Component View */}
      {expandedCategory === 'All Categories' && (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:justify-between md:text-left">
              <CardTitle className="flex items-center justify-center text-gray-800 md:justify-start">
                <Filter className="w-5 h-5 mr-2 text-emerald-600" />
                All Categories - Complete Data Table
              </CardTitle>
              <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-auto md:flex md:items-center md:space-x-2 md:gap-0">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={exportAllCategoriesData}
                  className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 md:w-auto"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <div className="w-full md:w-auto">
                  <Badge variant="secondary" className="flex justify-center bg-emerald-100 text-emerald-700 w-full md:w-auto">
                    {filteredAllComponents.length} of {getAllComponents().length} components
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="mt-3 flex flex-col gap-2 md:mt-4 md:flex-row md:items-center md:space-x-4">
              <div className="relative w-full md:flex-1 md:max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  placeholder="Search components..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-emerald-200 focus:border-emerald-400"
                />
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSort('name')}
                        className="font-semibold hover:bg-gray-100 p-0 h-auto"
                      >
                        Component
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSort('category')}
                        className="font-semibold hover:bg-gray-100 p-0 h-auto"
                      >
                        Category
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSort('value')}
                        className="font-semibold hover:bg-gray-100 p-0 h-auto"
                      >
                        {selectedImpactInfo?.shortName || 'Impact'}
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSort('percentage')}
                        className="font-semibold hover:bg-gray-100 p-0 h-auto"
                      >
                        Percentage
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAllComponents.map((component, index) => {
                    const impactValue = component.impacts[selectedImpact] || 0;
                    const percentage = (impactValue / dataSource.totalImpacts[selectedImpact]) * 100;
                    
                    return (
                      <TableRow 
                        key={component.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell className="font-medium">{component.name}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary" 
                            className={getCategoryBadgeColor(component.category)}
                          >
                            {component.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {convertToTonnes(impactValue)}
                          <span className="text-xs text-gray-500 ml-1">
                            CO2-eq
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(percentage * 2, 100)}%` }}
                              />
                            </div>
                            <span className="font-medium text-gray-900 min-w-[3rem]">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

                        {filteredAllComponents.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No components found matching your search.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {expandedCategory && expandedCategory !== 'All Categories' && componentsByCategory[expandedCategory] && (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center text-gray-800">
              {React.createElement(categoryIcons[expandedCategory] || Building, { 
                className: "w-5 h-5 mr-2 text-emerald-600" 
              })}
              {expandedCategory} Components
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {componentsByCategory[expandedCategory]
                .sort((a, b) => {
                  // Sort components by impact value in descending order (highest first)
                  const impactA = a.impacts[selectedImpact] || 0;
                  const impactB = b.impacts[selectedImpact] || 0;
                  return impactB - impactA;
                })
                .map((component) => {
                const impactValue = component.impacts[selectedImpact] || 0;
                const percentage = (impactValue / dataSource.totalImpacts[selectedImpact]) * 100;

                return (
                  <div 
                    key={component.id}
                    className="group p-4 rounded-lg border border-gray-200 hover:border-emerald-300 transition-all duration-300 hover:shadow-md bg-white/50"
                  >
                    <div className="mb-3">
                        <h4 className="font-semibold text-gray-800 group-hover:text-emerald-600 transition-colors">
                          {component.name}
                        </h4>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Impact Value</span>
                        <div className="text-right">
                                                   <span className="font-bold text-gray-900">
                           {convertToTonnes(impactValue)}
                         </span>
                         <span className="text-xs text-gray-500 ml-1">
                           CO2-eq
                         </span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">% of Total</span>
                          <span className="text-xs font-medium text-gray-700">
                            {percentage.toFixed(2)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-full bg-gradient-to-r ${categoryColors[expandedCategory]} rounded-full transition-all duration-1000 ease-out`}
                            style={{ width: `${Math.min(percentage * 2, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ComponentBreakdown;
